import { useEffect, useState } from 'react';
import { getDrivers, createDriver, updateDriver } from '../../services/api';
import toast from 'react-hot-toast';
import { Plus, X } from 'lucide-react';

const STATUS_COLORS = { available: 'bg-green-100 text-green-700', on_trip: 'bg-yellow-100 text-yellow-700', off_duty: 'bg-gray-100 text-gray-600' };

export default function AdminDrivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', license_number: '', license_expiry: '' });
  const [saving, setSaving] = useState(false);

  const load = () => { getDrivers().then(r => setDrivers(r.data.drivers)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const handleOpen = (d = null) => {
    setEditDriver(d);
    setForm(d ? { first_name: d.first_name, last_name: d.last_name, email: d.email, phone: d.phone, license_number: d.license_number, license_expiry: d.license_expiry } : { first_name: '', last_name: '', email: '', phone: '', license_number: '', license_expiry: '' });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editDriver) { await updateDriver(editDriver.id, form); toast.success('Driver updated.'); }
      else { await createDriver(form); toast.success('Driver added!'); }
      setShowModal(false);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (id, status) => {
    const d = drivers.find(dr => dr.id === id);
    await updateDriver(id, {
      first_name: d.first_name,
      last_name: d.last_name,
      phone: d.phone,
      status,
      is_active: d.is_active,
    });
    setDrivers(ds => ds.map(d => d.id === id ? { ...d, status } : d));
    toast.success('Driver status updated.');
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-gray-900 font-heading">Drivers</h1><p className="text-sm text-gray-500">{drivers.length} drivers on roster</p></div>
        <button onClick={() => handleOpen()} className="btn-primary text-sm flex items-center gap-2"><Plus size={16} /> Add Driver</button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <p className="text-gray-400 text-sm col-span-3 text-center py-10">Loading...</p> :
          drivers.map(d => (
            <div key={d.id} className="card border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold">
                  {d.first_name[0]}{d.last_name[0]}
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[d.status]}`}>{d.status.replace('_', ' ')}</span>
              </div>
              <h3 className="font-bold text-gray-900">{d.first_name} {d.last_name}</h3>
              <p className="text-sm text-gray-500">{d.phone}</p>
              <p className="text-sm text-gray-500">{d.email}</p>
              <p className="text-xs text-gray-400 mt-1">License: {d.license_number}</p>
              <p className="text-xs text-gray-400">Expires: {d.license_expiry}</p>
              <p className="text-xs text-gray-400 mt-1">⭐ {d.rating} · {d.total_trips} trips</p>
              <div className="mt-4 flex gap-2 flex-wrap">
                <select value={d.status} onChange={e => handleStatusChange(d.id, e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-400 bg-white text-gray-700">
                  <option value="available">Available</option>
                  <option value="on_trip">On Trip</option>
                  <option value="off_duty">Off Duty</option>
                </select>
                <button onClick={() => handleOpen(d)} className="text-xs text-purple-700 border border-purple-200 px-2.5 py-1.5 rounded-lg hover:bg-purple-50 transition-colors">Edit</button>
              </div>
            </div>
          ))
        }
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 font-heading">{editDriver ? 'Edit Driver' : 'Add Driver'}</h2>
              <button onClick={() => setShowModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="label">First Name</label><input className="input-field" required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} /></div>
                <div><label className="label">Last Name</label><input className="input-field" required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} /></div>
              </div>
              <div><label className="label">Email</label><input type="email" className="input-field" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="label">Phone</label><input className="input-field" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="label">License Number</label><input className="input-field" required value={form.license_number} onChange={e => setForm({ ...form, license_number: e.target.value })} /></div>
              <div><label className="label">License Expiry</label><input type="date" className="input-field" required value={form.license_expiry} onChange={e => setForm({ ...form, license_expiry: e.target.value })} /></div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn-outline text-sm">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary text-sm flex items-center gap-2">
                  {saving && <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                  {editDriver ? 'Save Changes' : 'Add Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
